import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { makeRequest2 } from "../../../../../../utils/makeRequest";
import { useFacilityStore } from "../../../../../../app/z-store/store";
import { toastify } from "../../../../../../utils/toast";

const RANGE_OPTIONS = ["6h", "24h", "7d", "30d", "90d"];

const RANGE_MS = {
  "6h": 6 * 60 * 60 * 1000,
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
  "90d": 90 * 24 * 60 * 60 * 1000,
};

const BASE_ENDPOINT =
  "/api/app/power_meter_management/controllers/history_data";

const ConsumptionReadingsTab = () => {
  const { meterId } = useParams();

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("single_day");
  const [selectedRange, setSelectedRange] = useState("24h");
  const [consumptionData, setConsumptionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTable, setShowTable] = useState(true);

  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  // Choose the API endpoint based on the requested date range.
  // One day uses single-day readings; shorter ranges use daily data;
  // longer ranges use monthly data.
  const getPeriodForRange = (start, end) => {
    if (!start && !end) return "single_day";

    if (start && end && start === end) {
      return "single_day";
    }

    if (start && end) {
      const first = new Date(`${start}T00:00:00`);
      const last = new Date(`${end}T00:00:00`);
      const days = Math.floor((last - first) / (24 * 60 * 60 * 1000)) + 1;

      return days <= 31 ? "daily" : "monthly";
    }

    return "daily";
  };

  const getEndpoint = (period) => {
    switch (period) {
      case "daily":
        return `${BASE_ENDPOINT}/meter_daily_consumption/${meterId}`;
      case "monthly":
        return `${BASE_ENDPOINT}/meter_monthly_consumption/${meterId}`;
      default:
        return `${BASE_ENDPOINT}/meter_single_day_consumption/${meterId}`;
    }
  };

  const fetchConsumptionData = async (
    period = selectedPeriod,
    start = startDate,
    end = endDate,
  ) => {
    try {
      setLoading(true);

      let endpoint = getEndpoint(period);
      const params = new URLSearchParams();

      if (start) params.append("start_date", start);
      if (end) params.append("end_date", end);

      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }

      const response = await makeRequest2(endpoint, "GET");

      const historyData =
        response?.data?.data ??
        (Array.isArray(response?.data) ? response.data : null) ??
        (Array.isArray(response?.data?.data?.data)
          ? response.data.data.data
          : null);

      if (response?.success || Array.isArray(historyData)) {
        setConsumptionData(Array.isArray(historyData) ? historyData : []);
      } else {
        toastify(
          response?.error || "Failed to fetch consumption data",
          "error",
        );
        setConsumptionData([]);
      }
    } catch (error) {
      console.error("Error fetching consumption data:", error);
      toastify("An error occurred while fetching consumption data", "error");
      setConsumptionData([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load.
  useEffect(() => {
    if (meterId && selectedFacilityId) {
      fetchConsumptionData("single_day", "", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meterId, selectedFacilityId]);

  const handleFilter = () => {
    if (startDate && endDate && startDate > endDate) {
      toastify("Start date cannot be after end date", "error");
      return;
    }

    const period = getPeriodForRange(startDate, endDate);

    setSelectedPeriod(period);
    setSelectedRange("90d");

    // Pass the new values directly instead of relying on asynchronous
    // state updates.
    fetchConsumptionData(period, startDate, endDate);
  };

  const formatDate = (date) => {
    if (!date) return "-";

    if (selectedPeriod === "monthly") return date;

    const parsed = new Date(date);

    return Number.isNaN(parsed.getTime())
      ? date
      : parsed.toLocaleDateString("en-US");
  };

  const formatTime = (time) => {
    if (!time || selectedPeriod !== "single_day") return "-";
    return time;
  };

  const dateBodyTemplate = (row) =>
    formatDate(selectedPeriod === "monthly" ? row.yearMonth : row.date);

  const timeBodyTemplate = (row) => formatTime(row.time);

  const statusBodyTemplate = (row) => row.status || "-";

  const readingBodyTemplate = (row) => {
    if (selectedPeriod === "monthly") {
      return `${row.initialReading ?? 0} - ${row.finalReading ?? 0}`;
    }

    return row.reading ?? 0;
  };

  const consumptionBodyTemplate = (row) =>
    selectedPeriod === "monthly" ? (row.consumption ?? 0) : "-";

  const renderColumns = () => {
    const columns = [
      <Column
        key="reading"
        field="reading"
        sortable
        header={
          selectedPeriod === "monthly" ? "Reading Range (kWh)" : "Reading (kWh)"
        }
        body={readingBodyTemplate}
      />,
    ];

    if (selectedPeriod === "monthly") {
      columns.push(
        <Column
          key="consumption"
          field="consumption"
          sortable
          header="Consumption (kWh)"
          body={consumptionBodyTemplate}
        />,
      );
    }

    columns.push(
      <Column
        key="date"
        field={selectedPeriod === "monthly" ? "yearMonth" : "date"}
        sortable
        header={selectedPeriod === "monthly" ? "Year-Month" : "Date"}
        body={dateBodyTemplate}
      />,
    );

    if (selectedPeriod === "single_day") {
      columns.push(
        <Column
          key="time"
          field="time"
          sortable
          header="Time"
          body={timeBodyTemplate}
        />,
      );
    }

    if (selectedPeriod !== "monthly") {
      columns.push(
        <Column
          key="status"
          field="status"
          sortable
          header="Status"
          body={statusBodyTemplate}
        />,
      );
    }

    return columns;
  };

  const getExportRows = () =>
    consumptionData.map((row) => {
      if (selectedPeriod === "monthly") {
        return {
          "Reading Range (kWh)": `${row.initialReading ?? 0} - ${row.finalReading ?? 0}`,
          "Consumption (kWh)": row.consumption ?? 0,
          "Year-Month": row.yearMonth || "-",
        };
      }

      const result = {
        "Reading (kWh)": row.reading ?? 0,
        Date: formatDate(row.date),
      };

      if (selectedPeriod === "single_day") {
        result.Time = row.time || "-";
      }

      result.Status = row.status || "-";

      return result;
    });

  const handleExportExcel = () => {
    if (!consumptionData.length) {
      toastify("No data to export", "warn");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(getExportRows());
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Consumption");

    XLSX.writeFile(workbook, `meter_${meterId}_consumption.xlsx`);
  };

  const handleExportPDF = () => {
    if (!consumptionData.length) {
      toastify("No data to export", "warn");
      return;
    }

    const rows = getExportRows();
    const headers = Object.keys(rows[0]);
    const body = rows.map((row) => headers.map((key) => row[key]));

    const doc = new jsPDF({ orientation: "landscape" });

    doc.setFontSize(14);
    doc.text("Power Meter Consumption Report", 14, 15);
    doc.setFontSize(10);
    doc.text(`Meter ID: ${meterId}`, 14, 22);
    doc.text(`Period: ${selectedPeriod}`, 14, 27);

    if (startDate || endDate) {
      doc.text(`Range: ${startDate || "-"} to ${endDate || "-"}`, 14, 32);
    }

    autoTable(doc, {
      head: [headers],
      body,
      startY: 37,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save(`meter_${meterId}_consumption.pdf`);
  };

  const graphData = useMemo(() => {
    const now = Date.now();
    const cutoff = now - RANGE_MS[selectedRange];

    return consumptionData
      .map((row) => {
        let timestamp = null;

        const rawTimestamp = row.timestamp ?? row.ts;

        if (rawTimestamp !== undefined && rawTimestamp !== null) {
          timestamp =
            typeof rawTimestamp === "number"
              ? rawTimestamp < 1e12
                ? rawTimestamp * 1000
                : rawTimestamp
              : new Date(rawTimestamp).getTime();
        } else if (row.date) {
          timestamp = new Date(
            `${row.date}${row.time ? `T${row.time}` : "T00:00:00"}`,
          ).getTime();
        } else if (row.yearMonth) {
          timestamp = new Date(`${row.yearMonth}-01`).getTime();
        }

        const rawValue =
          selectedPeriod === "monthly" ? row.consumption : row.reading;

        const value =
          rawValue !== undefined &&
          rawValue !== null &&
          rawValue !== "" &&
          Number.isFinite(Number(rawValue))
            ? Number(rawValue)
            : null;

        return { timestamp, value };
      })
      .filter(
        (point) => Number.isFinite(point.timestamp) && point.value !== null,
      )
      .filter(
        (point) => selectedPeriod === "monthly" || point.timestamp >= cutoff,
      )
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [consumptionData, selectedRange, selectedPeriod]);

  const chartTicks = useMemo(() => {
    if (!graphData.length) return [];

    const min = graphData[0].timestamp;
    const max = graphData[graphData.length - 1].timestamp;

    if (min === max) return [min];

    const step = RANGE_MS[selectedRange] / 5;
    const ticks = [];

    for (let tick = Math.ceil(min / step) * step; tick <= max; tick += step) {
      ticks.push(tick);
    }

    return ticks;
  }, [graphData, selectedRange]);

  const downloadCSV = () => {
    if (!graphData.length) {
      toastify("No graph data to export", "warn");
      return;
    }

    const rows = graphData.map((point) => ({
      Time: new Date(point.timestamp).toLocaleString(),
      [selectedPeriod === "monthly" ? "Consumption (kWh)" : "Reading (kWh)"]:
        point.value,
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `meter_${meterId}_consumption.csv`;
    link.click();

    URL.revokeObjectURL(url);
  };

  const chartTitle =
    selectedPeriod === "monthly"
      ? "Consumption history (kWh)"
      : "Reading history (kWh)";

  return (
    <div className="tab-pane show active" role="tabpanel">
      <style>{`
                .consumption-history-card {
                    border: 1px solid #e1e6ed;
                    border-radius: 7px;
                    overflow: hidden;
                    background: #fff;
                }

                .consumption-history-header {
                    padding: 18px 22px;
                    border-bottom: 1px solid #e1e6ed;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 16px;
                    flex-wrap: wrap;
                }

                .consumption-range-buttons {
                    display: inline-flex;
                    border: 1px solid #9ca3af;
                    border-radius: 5px;
                    overflow: hidden;
                }

                .consumption-range-buttons button {
                    background: #fff;
                    color: #475569;
                    border: 0;
                    border-right: 1px solid #9ca3af;
                    padding: 5px 9px;
                    font-size: 12px;
                }

                .consumption-range-buttons button:last-child {
                    border-right: 0;
                }

                .consumption-range-buttons button.active {
                    background: #4f63ed;
                    color: #fff;
                }

                .consumption-chart-area {
                    padding: 12px 18px 4px;
                }

                .consumption-chart-actions {
                    display: flex;
                    gap: 18px;
                    align-items: center;
                    padding: 4px 22px 16px;
                }

                .consumption-chart-actions button {
                    border: 0;
                    background: transparent;
                    color: #3978ff;
                    padding: 0;
                    font-size: 12px;
                }

                .consumption-history-table {
                    padding: 0 22px 18px;
                }

                .consumption-history-table
                .p-datatable .p-datatable-thead > tr > th {
                    background: #eff6ff;
                    color: #334155;
                }

                .consumption-history-table
                .p-datatable .p-datatable-tbody > tr:nth-child(even) {
                    background: #e7f1f7;
                }

                .consumption-history-table
                .p-datatable .p-datatable-tbody > tr > td {
                    border: 0;
                }
            `}</style>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <div className="row align-items-end">
                <div className="col-md-4 mt-2">
                  <label htmlFor="consumption-start-date">Start Date</label>
                  <input
                    id="consumption-start-date"
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>

                <div className="col-md-4 mt-2">
                  <label htmlFor="consumption-end-date">End Date</label>
                  <input
                    id="consumption-end-date"
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>

                <div className="col-md-4 mt-2">
                  <Button
                    label="Filter"
                    className="w-100"
                    onClick={handleFilter}
                    loading={loading}
                  />
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button
                  label="Export Excel"
                  className="p-button-outlined p-button-success"
                  onClick={handleExportExcel}
                  disabled={!consumptionData.length}
                />

                <Button
                  label="Export PDF"
                  className="p-button-outlined p-button-danger"
                  onClick={handleExportPDF}
                  disabled={!consumptionData.length}
                />
              </div>
            </div>

            <div className="card-body">
              <section className="consumption-history-card">
                <div className="consumption-history-header">
                  <div>
                    <h6 className="mb-1">{chartTitle}</h6>
                    <small className="text-muted">Readings over time</small>
                  </div>

                  <div
                    className="consumption-range-buttons"
                    aria-label="Graph time range"
                  >
                    {RANGE_OPTIONS.map((range) => (
                      <button
                        type="button"
                        key={range}
                        className={selectedRange === range ? "active" : ""}
                        onClick={() => setSelectedRange(range)}
                      >
                        {range}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="consumption-chart-area">
                  {graphData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <AreaChart
                        data={graphData}
                        margin={{
                          top: 12,
                          right: 48,
                          bottom: 0,
                          left: -8,
                        }}
                      >
                        <defs>
                          <linearGradient
                            id="consumptionHistoryFill"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="0%"
                              stopColor="#2a78d6"
                              stopOpacity={0.28}
                            />
                            <stop
                              offset="100%"
                              stopColor="#2a78d6"
                              stopOpacity={0.02}
                            />
                          </linearGradient>
                        </defs>

                        <CartesianGrid vertical={false} stroke="#e6e5e0" />

                        <XAxis
                          dataKey="timestamp"
                          type="number"
                          scale="time"
                          domain={["dataMin", "dataMax"]}
                          ticks={chartTicks}
                          tickFormatter={(value) =>
                            new Date(value).toLocaleString(
                              [],
                              selectedRange === "6h" || selectedRange === "24h"
                                ? {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  }
                                : {
                                    day: "numeric",
                                    month: "short",
                                  },
                            )
                          }
                          tick={{
                            fill: "#7a7974",
                            fontSize: 11,
                          }}
                          axisLine={{
                            stroke: "#e6e5e0",
                          }}
                          tickLine={false}
                          minTickGap={30}
                        />

                        <YAxis
                          tick={{
                            fill: "#7a7974",
                            fontSize: 11,
                          }}
                          axisLine={false}
                          tickLine={false}
                          width={48}
                        />

                        <Tooltip
                          labelFormatter={(value) =>
                            new Date(value).toLocaleString()
                          }
                          formatter={(value) => [
                            value,
                            selectedPeriod === "monthly"
                              ? "Consumption (kWh)"
                              : "Reading (kWh)",
                          ]}
                        />

                        <Area
                          type="monotone"
                          dataKey="value"
                          stroke="#2a78d6"
                          strokeWidth={2}
                          fill="url(#consumptionHistoryFill)"
                          isAnimationActive={false}
                          activeDot={{
                            r: 5,
                            stroke: "#fff",
                            strokeWidth: 2,
                            fill: "#2a78d6",
                          }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center text-muted py-5">
                      {loading
                        ? "Loading readings..."
                        : "No readings available for this time range."}
                    </div>
                  )}
                </div>

                <div className="consumption-chart-actions">
                  <button
                    type="button"
                    onClick={() => setShowTable((visible) => !visible)}
                  >
                    <i
                      className={`ti ${
                        showTable ? "ti-layout-grid" : "ti-table"
                      } me-1`}
                    />
                    {showTable ? "Hide table" : "Show as table"}
                  </button>

                  <button type="button" onClick={downloadCSV}>
                    <i className="ti ti-download me-1" />
                    Download CSV
                  </button>
                </div>

                {showTable && (
                  <div className="consumption-history-table">
                    <DataTable
                      value={consumptionData}
                      loading={loading}
                      emptyMessage="No consumption readings found."
                      sortMode="multiple"
                      paginator
                      rows={10}
                      stripedRows
                      tableStyle={{
                        minWidth: "50rem",
                      }}
                    >
                      {renderColumns()}
                    </DataTable>
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsumptionReadingsTab;
